// AiSettingsButton — THE single AI-settings trigger, shared by the assistant
// chat header (_simone/Chat.js — the composer row carries none since
// 2026-09-26) and the canvas prompt bar's ParamsBtn (workspace/app.js) — one
// glyph, one label, one behaviour
// (2026-08-12 per Nika "canvasPromptTextarea and ai assistant should be
// using same model switcher and settings buttons"). The PANEL was already
// shared (AiSettingsPanel — both surfaces mount the same body); this
// unifies the BUTTON that opens it.
//
// Context-aware open — one component, the right surface either side:
//   • inside a Chat surface (an ancestor carries `chatScope` — the same
//     walk as Chat.js chatScopeOf) it toggles root.showSettings, which
//     Chat's own Settings slot (SettingsDrawer → AiSettingsPanel) renders
//     over the assistant. Works on every Chat consumer, including the
//     plugin-less ones (standalone simone, chrome-connect, ext-ui).
//   • outside (the shell composer) it opens the @symbo.ls/dropdown panel
//     with the SAME AiSettingsPanel body anchored to the button — the
//     house openDropdown pattern.
export const AiSettingsButton = {
  // Geometry + states come from the assistant chrome's one icon-button
  // primitive (36px pill, iconMd glyph, inactive→title ink, hover wash), so
  // this button is the same control in the drawer header, the composer bar
  // and the home composer's params slot. Only the icon and the behaviour
  // live here.
  extends: 'AssistantIconButton',
  // FA204 / frank trap — helpers live in scope, not module scope (they read
  // as undefined once this component is frank.toJSON'd for delivery).
  scope: {
    inChatSurface(el) {
      let n = el
      while (n) {
        if (n.chatScope) return true
        n = n.parent
      }
      return false
    },
    toggleChatSettings(s) {
      if (s.root && s.root.update) s.root.update({ showSettings: !s.root.showSettings })
    }
  },
  icon: 'preferences',
  title: (el) => el.call('polyglot', 'ai.settings.panelLabel'),
  'aria-haspopup': 'menu',
  // Chat surface: pointer = onMousedown (survives the chrome re-render
  // dead-click window, worst under PageSlot churn); keyboard = detail-0
  // click. Guarded so one gesture can't double-toggle showSettings
  // (open-then-close = "AI settings doesn't open").
  onMousedown: (e, el, s) => {
    if (e.button !== 0) return
    if (!el.scope.inChatSurface(el)) return
    el.scope.toggleChatSettings(s)
  },
  onClick: (e, el, s) => {
    if (el.scope.inChatSurface(el)) {
      if (e.detail !== 0) return
      el.scope.toggleChatSettings(s)
      return
    }
    // Shell surface — the dropdown plugin owns positioning, outside-click
    // and ESC dismiss. The dropdown Panel is deliberately padding:0 (the
    // content owns its chrome — DropdownRoot.js), so the caller supplies
    // the panel's own padding here (the SettingsDrawer sheet pads via its
    // Sheet instead — same body, two hosts). Geometry matches THE AI menu
    // system (AiMenu.js AiMenuPanel): minWidth G / radiusCard / 60vh cap —
    // the same values the ModelBtn dropdown and the inline assistant panels
    // carry, so every AI menu is one shape.
    e.preventDefault()
    e.stopPropagation()
    el.call('openDropdown', {
      id: 'aiParams',
      anchorEl: el,
      placement: 'top-end',
      minWidth: 'G',
      maxHeight: '60dvh',
      round: 'radiusCard',
      // Shell paint opt-in — AiSettingsPanel's body paints no card of its
      // own (its rows carry their own pills); the plugin Panel is
      // chromeless by default (FW-DROPDOWN-PANEL-STALE-SHELL-PAINT-1).
      theme: 'canvas-card-glass',
      shadow: 'overlay',
      content: { extends: 'AiSettingsPanel', padding: 'Z' }
    })
  }
}
